import { Platform, View } from 'react-native';
import React, { useCallback, useEffect, useState } from 'react';
import {
  Banner,
  Footer,
  Input,
  KeyboardScreenContainer,
  Text,
} from '~/components';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import useMarisaAutoForm from '~/stores/marisaAutoForm';
import type { MarisaAutoStepsProps } from '../..';
import { isCpf } from '~/utils/yup';
import type { AnonymousUserState } from '~/storage/types';
import { LocalStorage } from '~/storage/LocalStorage';
import { ANONYMOUS_USER } from '~/storage/constants';
import { isWeb } from '~/utils/device';
import { useSalesFunnel } from '~/stores';

const schema = yup
  .object()
  .shape({
    document: isCpf(),
  })
  .required();

interface DocumentForm {
  document: string;
}

const Document: React.FC<MarisaAutoStepsProps> = ({ onContinue }) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<DocumentForm>({
    resolver: yupResolver(schema),
  });

  const { setDocument } = useMarisaAutoForm();
  const { setInfos } = useSalesFunnel();

  const [isCpfInputDisabled, setIsCpfInputDisabled] = useState(false);

  const onSubmit = useCallback(
    (data: DocumentForm) => {
      setDocument(data.document);
      setInfos({ documentNumber: data.document });
      onContinue();
    },
    [setDocument, setInfos, onContinue]
  );

  useEffect(() => {
    async function verifyAnonymousUser() {
      const anonymousUserStorage = await LocalStorage.get<AnonymousUserState>(
        ANONYMOUS_USER
      );

      if (anonymousUserStorage && !anonymousUserStorage?.isNotInsured) {
        setValue('document', anonymousUserStorage.document);
      }

      setIsCpfInputDisabled(
        !!anonymousUserStorage && !anonymousUserStorage.isNotInsured && isWeb
      );
    }

    verifyAnonymousUser();
  }, [setValue]);

  return (
    <KeyboardScreenContainer
      footer={
        <View
          className={`flex xl:hidden ${
            Platform.OS === 'web' ? 'py-4 px-6' : ''
          }`}
        >
          <Footer onSubmit={handleSubmit(onSubmit)} />
        </View>
      }
    >
      <View className="flex-1 container mx-auto xl:flex-row xl:pt-12 xl:pb-24">
        <Banner type="marisa-auto" />

        <View className="mt-8 xl:mt-0 px-6 flex-1 xl:ml-24 mb-4">
          <Text className="text-xl mb-4 text-gray-600">Qual o seu CPF?</Text>

          <Text className="text-gray-400 text-sm mb-8">
            O documento deve ser do condutor principal, aquele quem dirige o
            carro na maior parte do tempo.
          </Text>

          <Input
            control={control}
            name="document"
            label="Qual o seu CPF?"
            placeholder="Insira os números do CPF sem pontos"
            maskConfig={{ type: 'cpf' }}
            maxLength={14}
            keyboardType="decimal-pad"
            error={errors.document?.message}
            disabled={isCpfInputDisabled}
          />

          <Footer className="hidden" onSubmit={handleSubmit(onSubmit)} />
        </View>
      </View>
    </KeyboardScreenContainer>
  );
};

export default Document;
